import { Link, useRouterState } from '@tanstack/react-router'

const containerW = 'mx-auto max-w-7xl px-6 sm:px-8 lg:px-12'

export const LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Service' },
  { to: '/dpa', label: 'Data Processing Agreement' },
  { to: '/cookie-policy', label: 'Cookie Policy' },
] as const

interface LegalPageShellProps {
  title: string
  updated: string
  children: React.ReactNode
}

/**
 * Shared dark chrome for the marketing legal pages (privacy, terms, cookies).
 * The landing header in __root.tsx supplies the top nav; this component renders
 * the page body and a compact footer with cross-links between the three pages.
 */
export function LegalPageShell({ title, updated, children }: LegalPageShellProps) {
  const { location } = useRouterState()
  const pathname = location.pathname

  return (
    <div className="relative min-h-svh overflow-hidden bg-[#0B1120]">
      {/* Decorative glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 size-[600px] rounded-full bg-[#00E5A0]/8 blur-[140px]" />

      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20">
        <div className="mx-auto max-w-3xl px-6 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-[#00E5A0] transition-colors"
          >
            ← Back to home
          </Link>

          <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight tracking-tight text-white">
            {title}
          </h1>
          <p className="mt-3 text-sm text-white/40">Last updated: {updated}</p>

          <div className="mt-10 space-y-8">{children}</div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className={`${containerW} py-10`}>
          <div className="flex flex-col items-center justify-between gap-5 sm:flex-row">
            <p className="text-xs text-white/30">
              &copy; {new Date().getFullYear()} PeerPass. All rights reserved.
            </p>
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {LEGAL_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-xs transition-colors ${
                    pathname === link.to ? 'text-[#00E5A0]' : 'text-white/40 hover:text-white/70'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </footer>
    </div>
  )
}